import { ChangeDetectorRef, Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ACTUAL_ENV } from '../../config/config';

// Model
import { User } from '../../models/user.model';

// Services
import { UserSessionService } from '../../services/service.index';
import { SettingsService } from '../../services/settings/settings.service';
import { SidebarService } from '../../services/shared/sidebar.service';
import { MixpanelService } from 'src/app/services/analytics/mixpanel.service';
import { ClientSystemService } from 'src/app/services/api-lab4u/client_system.service';

// Translator
import { LanguageService } from '../../services/language/language.service';
import { TranslocoService, TranslocoPipe } from '@jsverse/transloco';

// RxJS
import { Subscription } from 'rxjs';
import { mixpanelEvent } from 'src/app/config/mixpanel-events';
import { SidebarComponent } from '../sidebar/sidebar.component';
import { SafePipe } from '../../pipes/safe.pipe';
import { ModalAppService } from 'src/app/components/modal-app/modal-app.service';
import { AuthService } from 'src/app/services/auth/auth.service';

/**
 * Component to manage the header component
 */
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.css'],
  imports: [SidebarComponent, SafePipe, TranslocoPipe],
})
export class HeaderComponent implements OnInit, OnDestroy {
  environment: string = ACTUAL_ENV;

  /**
   * The user variable is declared
   * @param user Variable to save the connected user data
   */
  user!: User;
  /**
   * The theme variable is declared
   * @param theme Variable to keep page theme
   */
  theme?: string;
  /**
   * The color variable is declared
   * @param color Variable to keep colour
   */
  color!: string;
  /**
   * The App variable is declared
   * @param App Variable to save the name on the active app
   */
  App = '';

  userRol = this.UserSessionService.getRole();
  userInstitution!: string;

  userId = this.clientSystemService.getUserId();
  userEmail = this.UserSessionService.getUserEmail();
  /**
   * The language variable is declared
   * @param language Variable to save the actual language
   */
  language!: string;
  /**
   * The loading variable is declared
   * @param loading Variable to show/hide the loading
   */
  loading = false;
  redirect!: string;

  sidebarElements: any;

  private subscription?: Subscription;

  private readonly ROUTES = [
    '/general-documents',
    '/classrooms',
    '/home',
    '/topics',
    '/training-quizzes',
  ];

  /**
   * Following variables declared
   * @param UserSessionService Service that brings users data and functions
   * @param settings Service that works the page colour
   * @param languageService Service that works with the page language
   * @param translocoService Service that applies the selected language by the user
   * @param sidebarService Service that works with the sidebar and the app of the page
   */
  constructor(
    public clientSystemService: ClientSystemService,
    public UserSessionService: UserSessionService,
    private authService: AuthService,
    public settings: SettingsService,
    public languageService: LanguageService,
    private translocoService: TranslocoService,
    public sidebarService: SidebarService,
    public mixpanelService: MixpanelService,
    public router: Router,
    private modalAppService: ModalAppService,
    private cdr: ChangeDetectorRef,
  ) {
    this.selectLanguage();
  }

  /**
   * Data is saved in the {@link ../injectables/UserSessionService.html|UserSessionService.user} on the user variable
   *
   * Data is saved in the {@link ../injectables/LanguageService.html|languageService.getLanguage()} on the language variable
   *
   * Call the loadData() function to load all the necessary information
   */
  ngOnInit() {
    if (!this.initializeHeaderState()) {
      return;
    }
    this.setSidebarElements();
  }

  ngOnDestroy() {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * Method to initialize header and stop flow if an error ocurrs
   * @returns boolean as an error
   */
  initializeHeaderState(): boolean {
    try {
      this.App = this.sidebarService.getApp();
      if (this.App === 'undefined' || !this.App) this.App = 'LAB4PHYSICS';
      this.color = this.settings.getColor();
      this.user = this.UserSessionService.user;
      this.language = this.languageService.getLanguage();
      this.userInstitution = this.UserSessionService.getInstitution();
      this.showAppModal();
      this.loadData();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Method to set sidebar elements given the language
   */
  setSidebarElements() {
    const route = this.router.url.substring(0, 18);

    this.sidebarElements = this.sidebarService.getSidebarElements();

    if (this.isSpecialRoute(route)) {
      this.sidebarElements = this.sidebarService.getMenu();
    }
  }

  /**
   * Method to check if the current route is a special route for the language
   * @param route current route
   * @returns boolean that indicates if route falls in the condition
   */
  isSpecialRoute(route: string): boolean {
    return this.ROUTES.includes(route) || route.includes('/planning-ai');
  }

  /**
   * Function to change language with Transloco
   * @param language Variable to save the actual language of languageService
   */
  selectLanguage(language: string = this.languageService.getLanguage()) {
    this.translocoService.setActiveLang(language);
  }

  /**
   * Function to change language localStorage
   * @param language Variable to save the actual language
   */
  changeLanguage(language: string) {
    const route = this.router.url.substring(0, 13);
    if (language === 'es' || language === 'en') {
      this.language = language;
      this.languageService.applyLanguage(this.language);
      this.mixpanelService.track(
        mixpanelEvent.TOPBAR_CHANGE_LANGUAGE_SELECTED,
        {
          source_name: 'LAB4UPORTAL',
          status: this.language.toUpperCase(),
        },
      );
      window.location.reload();
    }
  }

  /**
   * Function that loads user data
   */
  loadData() {
    if (
      this.App.toUpperCase() === 'LAB4PHYSICS' ||
      this.App.toUpperCase() === 'LAB4CHEMISTRY' ||
      this.App.toUpperCase() === 'LAB4BIOLOGY'
    ) {
      this.loading = false;
      const storedUser = localStorage.getItem('user');
      if (storedUser) this.user = JSON.parse(storedUser);
      else
        this.subscription = this.UserSessionService.infoUser().subscribe(
          (resp: any) => {
            this.user = resp.response.user;
            this.cdr.markForCheck();
          },
        );
      this.loading = true;
    } else {
      return;
    }
  }

  /**
   * Function to apply colour and app selected
   * @param theme Variable that brings the colour selected by the user
   */
  changeColor(theme: string) {
    if (
      this.App.toUpperCase() === 'LAB4PHYSICS' ||
      this.App.toUpperCase() === 'LAB4CHEMISTRY' ||
      this.App.toUpperCase() === 'LAB4BIOLOGY'
    ) {
      const route = this.router.url.substring(0, 6);
      this.theme = theme;
      this.settings.applyTheme(this.theme);
      this.color = this.settings.getColor();

      if (this.color === 'red') {
        this.sidebarService.applyApp('LAB4PHYSICS');
        this.mixpanelService.track(`header_change_app_LAB4PHYSICS`, {});
      } else if (this.color === 'purple') {
        this.sidebarService.applyApp('LAB4CHEMISTRY');
        this.mixpanelService.track(`header_change_app_LAB4CHEMISTRY`, {});
      } else {
        this.sidebarService.applyApp('LAB4BIOLOGY');
        this.mixpanelService.track(`header_change_app_LAB4BIOLOGY`, {});
      }
      if (this.router.url.length > 25) {
        if (route === '/exper') {
          this.router.navigate(['/categories']);
        } else if (route === '/tool/') {
          this.router.navigate(['/tools']);
        } else {
          window.location.reload();
        }
      } else {
        window.location.reload();
      }
    } else {
      return;
    }
  }

  /**
   * Shows modal to select App if there isn't one already selected
   */
  showAppModal() {
    if (
      this.sidebarService.getApp() === 'undefined' ||
      this.sidebarService.getApp() === null
    ) {
      this.sidebarService.applyApp('LAB4PHYSICS');
      localStorage.setItem('App', JSON.stringify('LAB4PHYSICS'));
    } else this.modalAppService.hideModal();
  }

  headerGoHome() {
    this.mixpanelService.track('header_go_home', {
      'User role': this.userRol,
      "User's Institution": this.userInstitution,
    });
  }

  logOut() {
    this.authService.logout();

    this.mixpanelService.track('log_out', {
      'User role': this.userRol,
      "User's Institution": this.userInstitution,
    });
  }

  track() {
    this.mixpanelService.track('show_t_classroom_section', {
      "User's Institution": this.userInstitution,
    });
  }
}
